import React from 'react';
import { Link } from 'react-router-dom';
import { colourForShop } from '../../lib/adminQueries';
import { initials, STATUS_PILL } from '../../lib/shopDetail/helpers';

/**
 * Detail-page title bar: shop crest + name + status pills + the four
 * high-value admin actions (Visit shop, Act as shop, Suspend, Delete).
 */
export const DetailHead = ({ shop, onActAs, onSuspend, onResume, onDelete, isSuspended }) => {
  const sp = STATUS_PILL(shop.subscription_status);
  return (
    <div className="detail-head" data-testid="shop-detail-head">
      <span className="sq" style={{ background: colourForShop(shop) }}>{initials(shop.name).charAt(0)}</span>
      <div className="ti">
        <b>{shop.name}</b>
        <small>{shop.subdomain}.instacut.co.uk</small>
      </div>
      <span className={`pill ${sp.cls}`}>{sp.label}</span>
      <span className="pill prem" style={{ textTransform: 'capitalize' }}>{shop.plan}</span>
      <div className="actions">
        <a className="btn s" href={`/book/${shop.subdomain}`} target="_blank" rel="noreferrer" data-testid="visit-public-booking">Visit shop</a>
        <button
          className="btn p"
          onClick={onActAs}
          data-testid="act-as-shop-btn"
          title="Sign in to this shop's dashboard on behalf of the owner"
        >
          Act as this shop →
        </button>
        {isSuspended ? (
          <button
            className="btn s"
            onClick={onResume}
            data-testid="resume-shop-btn"
            title="Create a fresh Stripe subscription and reactivate the shop"
            style={{ color: '#0f8a3d' }}
          >
            Resume shop
          </button>
        ) : (
          <button
            className="btn s"
            onClick={onSuspend}
            data-testid="suspend-shop-btn"
          >
            Suspend shop
          </button>
        )}
        <button
          className="btn s"
          onClick={onDelete}
          data-testid="delete-shop-btn"
          disabled={!isSuspended}
          title={isSuspended ? 'Archive & delete this shop' : 'Suspend the shop first'}
          style={{ color: isSuspended ? '#b03a2e' : undefined }}
        >
          Delete shop
        </button>
      </div>
    </div>
  );
};

/**
 * Two rows of 4-up dstats: booking/revenue KPIs on top, payment-mix on
 * the bottom. Both are pure display; the parent computes `stats` once
 * on load and passes it in.
 */
export const StatCards = ({ stats, fmtMoney }) => (
  <>
    <div className="dgrid" data-testid="shop-detail-stats">
      <div className="dstat"><div className="t">MRR contribution</div><div className="v">{fmtMoney(stats.mrr)}<small>/mo</small></div></div>
      <div className="dstat"><div className="t">Bookings · 30 days</div><div className="v">{stats.bookings30}</div></div>
      <div className="dstat"><div className="t">Completion rate</div><div className="v">{stats.completionRate.toFixed(0)}<small>%</small></div></div>
      <div className="dstat"><div className="t">Revenue · 30 days</div><div className="v">{fmtMoney(stats.lifetimeSpend)}</div></div>
    </div>

    {/* Audit — cash vs card vs Stripe split over the last 30 days.
        Data lives on `bookings.balance_paid_method` for at-chair
        collections; Stripe slice is deposit + paid-in-full via the
        wizard. Outstanding tracks bookings still owing money. */}
    <div className="dgrid" data-testid="shop-payment-mix">
      <div className="dstat" data-testid="shop-30d-cash">
        <div className="t">Cash · 30 days</div>
        <div className="v">{fmtMoney(stats.cash30)}</div>
      </div>
      <div className="dstat" data-testid="shop-30d-card">
        <div className="t">Card at chair · 30 days</div>
        <div className="v">{fmtMoney(stats.card30)}</div>
      </div>
      <div className="dstat" data-testid="shop-30d-stripe">
        <div className="t">Prepaid via Stripe · 30 days</div>
        <div className="v">{fmtMoney(stats.stripe30)}</div>
      </div>
      <div className="dstat" data-testid="shop-30d-outstanding">
        <div className="t">Outstanding balance</div>
        <div className="v" style={{ color: stats.outstanding30 > 0 ? '#b91c1c' : undefined }}>{fmtMoney(stats.outstanding30)}</div>
      </div>
    </div>
  </>
);

/**
 * Right-rail activity panel. Timeline entries are pre-formatted by
 * the parent, this only styles them.
 */
export const ActivityTimeline = ({ timeline, fmtDateTime }) => (
  <div className="panel" data-testid="shop-timeline">
    <div className="ph"><h3>Activity</h3><span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>Most recent first</span></div>
    {timeline.length === 0 ? (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-mute)', fontSize: 'var(--fs-base)' }}>No activity yet.</div>
    ) : timeline.map((e, i) => (
      <div className="tline" key={i}>
        <span className="tm">{fmtDateTime(e.at)}</span>
        <span className={`ic ico-${e.tone}`}>•</span>
        <span className="tx">{e.text}</span>
      </div>
    ))}
  </div>
);

/**
 * Static "Account" info card — subdomain, plan, status, Stripe IDs.
 * All read-only; the mutation lives on Suspend / Delete buttons in
 * DetailHead.
 */
export const AccountInfoPanel = ({ shop, health }) => {
  const sp = STATUS_PILL(shop.subscription_status);
  return (
    <div className="panel" data-testid="shop-account-info">
      <div className="ph"><h3>Account</h3></div>
      <div className="infrow"><span className="k">Subdomain</span><span className="vv mono">{shop.subdomain}</span></div>
      <div className="infrow"><span className="k">Plan</span><span className="vv" style={{ textTransform: 'capitalize' }}>{shop.plan}</span></div>
      <div className="infrow"><span className="k">Status</span><span className="vv">{sp.label}</span></div>
      <div className="infrow"><span className="k">Interval</span><span className="vv" style={{ textTransform: 'capitalize' }}>{shop.subscription_interval || 'monthly'}</span></div>
      <div className="infrow"><span className="k">Stripe customer</span><span className="vv mono" style={{ fontSize: 'var(--fs-xs)' }}>{shop.stripe_customer_id || '—'}</span></div>
      <div className="infrow"><span className="k">Stripe Connect</span><span className="vv mono" style={{ fontSize: 'var(--fs-xs)' }}>{shop.stripe_account_id || '—'}</span></div>
      <div className="infrow"><span className="k">Health</span><span className="vv" style={{ textTransform: 'capitalize' }}>{health.band}</span></div>
      <div className="infrow"><span className="k">Created</span><span className="vv mono" style={{ fontSize: 'var(--fs-xs)' }}>{new Date(shop.created_at).toLocaleString('en-GB')}</span></div>
    </div>
  );
};

/**
 * Barbers-with-logins panel. Master admin can resend a fresh temp
 * password to any barber; their current password stops working right
 * away.
 */
export const BarbersWithLogins = ({ barbersWithLogin, onResend }) => {
  if (!barbersWithLogin.length) return null;
  return (
    <div className="panel" data-testid="barbers-with-logins">
      <div className="ph"><h3>Barbers with logins</h3></div>
      <div style={{ padding: '10px 16px 4px', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', borderBottom: '1px solid var(--line2)' }}>
        Resend a fresh temp password to any barber. Their current password will stop working immediately.
      </div>
      {barbersWithLogin.map((b) => (
        <div key={b.id} className="infrow" data-testid={`barber-login-row-${b.id}`}>
          <span className="vv">{b.name}</span>
          <button
            className="btn s"
            onClick={() => onResend(b.user_id, b.name)}
            data-testid={`barber-resend-credentials-${b.id}`}
          >
            Resend credentials
          </button>
        </div>
      ))}
    </div>
  );
};
